import { useQuery } from '@apollo/client'
import { AppBar, Tab, Tabs } from '@material-ui/core'
import { useTheme } from '@material-ui/core'
import { useMemo } from 'react'
import { Redirect, useParams } from 'react-router-dom'

import { buildFeatureSet, canAccessTab, getCredentials } from '../../cache/auth'
import Breadcrumbs from '../../components/Breadcrumb'
import { ACCOMMODATION_NAME_QUERY } from '../../graphql/accommodation'
import { TabPanel } from '../../helpers/TabPanel'
import { useRegisterSupportScreen } from '../../helpers/supportScreenContext'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import accommodationPagesRoleManagement from '../../staticRoleManagement/accommodationsRoleManagement'
import AccommodationBookingPermission from '../AccommodationBookingPermission'
import AccommodationFacilities from '../AccommodationFacilities'
import AccommodationForm from '../AccommodationForm'
import { AccommodationPMSMapping } from '../AccommodationPMSMapping'
import AccommodationProviderSubmit from '../AccommodationProviderSubmit'
import { UploadBoxForAccommodation } from '../AccommodationUpload'
import { UploadBoxForAccommodationCover } from '../AccommodationUploadCover'
import { UploadBoxForAccommodationLogo } from '../AccommodationUploadLogo'
import useStyles from './styles/index.styles'

export default function AccommodationTabs() {
  const classes = useStyles()
  const theme = useTheme()
  const { accID, page, propertyCode } = useParams<{
    accID: string
    propertyCode: string
    page: string
  }>()

  const { data, loading, error } = useQuery<{
    accommodationName: {
      name: string
      propertyCode?: string
    }
  }>(ACCOMMODATION_NAME_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      propertyCode: propertyCode
    }
  })

  useRegisterSupportScreen({
    affectedEntities:
      !loading && !error && data?.accommodationName?.propertyCode
        ? [
            {
              type: 'accommodation',
              code: data.accommodationName.propertyCode,
              label: data.accommodationName.name
            }
          ]
        : []
  })
  const cachedLevels = useMemo(
    () => [
      { label: getTranslate('Accommodations'), link: '/accommodations' },
      {
        label: data?.accommodationName.name || ''
      }
    ],
    [data]
  )

  const tabsList = [
    {
      label: getTranslate('Details'),
      value: 'details',
      link: `/accommodations/${accID}/${propertyCode}/details`,
      component: <AccommodationForm levels={cachedLevels} loading={loading} />
    },
    {
      label: getTranslate('Logo'),
      value: 'logo',
      link: `/accommodations/${accID}/${propertyCode}/logo`,
      component: (
        <UploadBoxForAccommodationLogo
          title={data?.accommodationName.name || ''}
        />
      )
    },
    {
      label: getTranslate('Images'),
      value: 'images',
      link: `/accommodations/${accID}/${propertyCode}/images`,
      component: (
        <UploadBoxForAccommodation title={data?.accommodationName.name || ''} />
      )
    },
    {
      label: getTranslate('Cover'),
      value: 'cover',
      link: `/accommodations/${accID}/${propertyCode}/cover`,
      component: (
        <UploadBoxForAccommodationCover
          title={data?.accommodationName.name || ''}
        />
      )
    },
    {
      label: getTranslate('Facilities'),
      value: 'facilities',
      link: `/accommodations/${accID}/${propertyCode}/facilities`,
      component: <AccommodationFacilities />
    },
    {
      label: getTranslate('PMS Mapping'),
      value: 'pms-mapping',
      link: `/accommodations/${accID}/${propertyCode}/pms-mapping`,
      component: (
        <AccommodationPMSMapping
          propertyCode={propertyCode}
          accommodationName={data?.accommodationName.name || ''}
          loadingForGetPropertyCode={loading}
        />
      )
    },
    {
      label: getTranslate('Booking Permission'),
      value: 'booking-permission',
      link: `/accommodations/${accID}/${propertyCode}/booking-permission`,
      component: <AccommodationBookingPermission />
    },
    {
      label: getTranslate('Provider Submit'),
      value: 'provider-submit',
      link: `/accommodations/${accID}/${propertyCode}/provider-submit`,
      component: (
        <AccommodationProviderSubmit
          title={data?.accommodationName.name || ''}
        />
      )
    }
  ]

  const credentials = getCredentials()
  const featureSet = buildFeatureSet(credentials)

  const routeConfig = accommodationPagesRoleManagement.find(
    (item) => item.route === '/accommodations/:accID/:propertyCode/:page'
  )

  const visibleTabs = tabsList.filter((tab) =>
    canAccessTab(routeConfig, tab.value, featureSet)
  )

  if (!canAccessTab(routeConfig, page, featureSet)) {
    return <Redirect to="/403" />
  }

  return (
    <div className={classes.root}>
      <AppBar className={classes.appBar} position="static" color="transparent">
        <Breadcrumbs levels={cachedLevels} loading={loading} />
        <Tabs
          value={page}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          className={classes.tabs}
          scrollButtons="auto">
          {visibleTabs.map((tabItem) => (
            <Tab
              key={tabItem.value}
              value={tabItem.value}
              className={classes.tab}
              label={tabItem.label}
              component={LocalizedLink}
              to={tabItem.link}
            />
          ))}
        </Tabs>
      </AppBar>
      {visibleTabs.map((tabItem) => (
        <TabPanel
          key={tabItem.value}
          value={page}
          index={tabItem.value}
          dir={theme.direction}
          className={classes.tabPanel}>
          {tabItem.component}
        </TabPanel>
      ))}
    </div>
  )
}
